import React, { useEffect, useRef } from "react";
import { Box, Theme, useMediaQuery, useTheme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { RailWidthState, useRailResize } from "./Rail.hooks";
import { getFrameEdges, FrameSlot, PaneSlot, frameSlotAttribute, useFrameStyles } from "./Rail.styles";

export interface PaneFrameProps {
    /** Where the pane is right now. "fullscreen" is the chat's expanded mode; "closed" parks it behind the edge of its default slot. */
    slot: FrameSlot;
    defaultSlot: PaneSlot;
    widthState: RailWidthState;
    /** Whether anything sits in the rail, a pane or the chat. A stage pane needs it for its right edge and its seam. */
    isRailOccupied?: boolean;
    /** Whether a pane is showing on the stage. A right pane needs it for its seam. */
    isStageOccupied?: boolean;
    /** Only for an occupant a plain border cannot draw, such as the chat's gradient edge. */
    borderTreatment?: SystemStyleObject<Theme>;
    resizeHandleColor: string;
    expandedDirection?: "row" | "column";
    children: React.ReactNode;
}

/** Below md there is no rail and no drag: every open pane fills the viewport and the spacer renders nothing. */
export function useIsRailOnSmallScreen(): boolean {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.down("md"));
}

/**
 * The shell every rail occupant sits in: the fixed geometry of its slot, the slide in and out, the move
 * between slots and the drag handle. Everything inside it, headers included, belongs to the occupant.
 */
function PaneFrame(props: PaneFrameProps) {
    const isSmallScreen = useIsRailOnSmallScreen();
    const geometrySlot = props.slot === "closed" ? props.defaultSlot : props.slot;
    // Read during render rather than from state: the answer is only needed for the styles of this very
    // commit, and putting it in state would render the pane once with the animation it is avoiding.
    const wasClosed = useRef(true);
    const isOpening = props.slot !== "closed" && wasClosed.current;
    useEffect(() => {
        wasClosed.current = props.slot === "closed";
    });
    // Each pane meets the other along its inner edge: the right pane's left, the stage pane's right. Taken
    // from the geometry slot rather than the frame, so a stage pane folded into the rail meets whatever a
    // right pane would.
    const styles = useFrameStyles({
        slot: props.slot,
        defaultSlot: props.defaultSlot,
        isSmallScreen,
        borderTreatment: props.borderTreatment,
        resizeHandleColor: props.resizeHandleColor,
        expandedDirection: props.expandedDirection ?? "column",
        isOpening,
    });
    const { onPointerDown } = useRailResize(props.widthState);
    const edges = getFrameEdges(geometrySlot, props.widthState.width, props.isRailOccupied ?? false, isSmallScreen);

    // Stays mounted while closed, parked past its edge: the pane slides open and shut because there is
    // something to slide. Returning null instead would make it appear in place with nothing to transition.
    //
    // The edges go through the style prop rather than sx so they land on the same inline properties the
    // drag writes to between commits.
    return (
        <Box {...{ [frameSlotAttribute]: geometrySlot }} sx={styles.pane} style={edges}>
            {props.slot === "right" && !isSmallScreen && <Box sx={styles.resizeHandle} onPointerDown={onPointerDown} />}
            {props.children}
        </Box>
    );
}

export default PaneFrame;
